import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useFavorites } from "@/context/FavoritesContext";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { toast } from "sonner";

export default function Favorites() {
  const { ids, count } = useFavorites();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const known = new Set(products.map((p) => p.id));
      const missing = ids.filter((id) => !known.has(id));
      if (missing.length === 0) { setLoading(false); return; }
      setLoading(true);
      const fetched = await Promise.all(
        missing.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
      );
      if (!cancelled) {
        setProducts((prev) => [...prev, ...fetched.filter(Boolean)]);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only show items still favorited (reacts to un-hearting)
  const visible = products.filter((p) => ids.includes(p.id));
  const inStock = visible.filter((p) => (p.available ?? p.quantity) > 0);

  const addAll = () => {
    inStock.forEach((p) => add(p, 1));
    toast.success(`${inStock.length} item(s) added to cart`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
      <div className="border-b-2 border-ink pb-4 mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 bg-danger text-white font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1 mb-3">
            <Heart className="h-3.5 w-3.5 fill-white" /> Saved
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">YOUR FAVORITES</h1>
          <p className="font-mono text-sm text-subtle mt-2">{count} saved · jump back and buy in one click.</p>
        </div>
        {inStock.length > 0 && (
          <button data-testid="add-all-favorites" onClick={addAll} className="flex items-center gap-2 bg-ink text-paper px-5 h-11 font-mono text-xs uppercase tracking-[0.15em] shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform">
            <ShoppingBag className="h-4 w-4" /> Add All In-Stock ({inStock.length})
          </button>
        )}
      </div>

      {loading ? (
        <div className="font-mono text-subtle">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-subtle p-12 text-center">
          <Heart className="h-8 w-8 mx-auto text-subtle" />
          <p className="font-mono text-sm text-subtle mt-4">No favorites yet. Tap the heart on any product to save it here.</p>
          <Link to="/" className="inline-block mt-6 bg-ink text-paper px-6 h-11 leading-[2.75rem] font-mono text-xs uppercase tracking-widest hover:bg-link transition-colors">Browse Catalog</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="favorites-grid">
          {visible.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
